import type {
  AuthConfig,
  AssociationMapping,
  BatchEncodeResponse,
  EncodingSystem,
  KnownNumberResponse,
  PrivacyNotice,
  SavedAssociation,
  SavedAssociationCollection,
  SessionDataExport,
  User,
} from "./types";
import {
  candidateMetadataMatchesProfile,
  DEFAULT_ENCODING_SYSTEM,
  getEncodingProfile,
} from "./encodingProfiles";

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const response = await fetch("/auth/config", {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to load authentication configuration");
  }
  return (await response.json()) as AuthConfig;
}

export async function fetchPrivacyNotice(): Promise<PrivacyNotice> {
  const response = await fetch("/privacy", { credentials: "include" });
  if (!response.ok) {
    throw new Error("Failed to load the privacy notice");
  }
  return (await response.json()) as PrivacyNotice;
}

export async function fetchSessionData(): Promise<SessionDataExport> {
  const response = await fetch("/privacy/me", { credentials: "include" });
  if (!response.ok) {
    throw new Error("Failed to export session data");
  }
  return (await response.json()) as SessionDataExport;
}

export async function eraseSessionData(): Promise<void> {
  const response = await fetch("/privacy/me", {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to erase session data");
  }
}

export async function logout(): Promise<void> {
  await fetch("/auth/logout", {
    method: "POST",
    credentials: "include",
  });
}

export async function fetchCurrentUser(): Promise<User | null> {
  const response = await fetch("/auth/me", {
    credentials: "include",
  });
  if (response.status === 401) {
    return null;
  }
  if (!response.ok) {
    throw new Error("Failed to load current user");
  }
  return (await response.json()) as User;
}

export async function encodeBatch(
  numbers: string[],
  options: {
    method?: "optimal" | "greedy" | "random";
    seed?: number;
    stochastic?: boolean;
    encoding_system?: EncodingSystem;
  } = {},
): Promise<BatchEncodeResponse> {
  const response = await fetch("/ui-api/encode/batch", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "X-Nummem-UI": "browser",
    },
    body: JSON.stringify({ numbers, ...options }),
  });
  if (response.status === 401) {
    throw new Error("Authentication required");
  }
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as {
      detail?: string;
    } | null;
    throw new Error(error?.detail ?? "Encoding failed");
  }
  const payload = (await response.json()) as BatchEncodeResponse;
  validateBatchEncodingSystem(
    payload,
    options.encoding_system ?? DEFAULT_ENCODING_SYSTEM,
  );
  return payload;
}

export async function fetchKnownNumbers(): Promise<KnownNumberResponse> {
  const response = await fetch("/ui-api/known-numbers", {
    credentials: "include",
    headers: { "X-Nummem-UI": "browser" },
  });
  if (response.status === 401) {
    throw new Error("Authentication required");
  }
  if (!response.ok) {
    throw new Error("Failed to load known numbers");
  }
  return (await response.json()) as KnownNumberResponse;
}

export async function fetchSavedAssociations(
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
): Promise<SavedAssociationCollection> {
  const query = encodingSystem === "english"
    ? ""
    : `?encoding_system=${encodeURIComponent(encodingSystem)}`;
  const response = await fetch(`/ui-api/saved-associations${query}`, {
    credentials: "include",
    headers: { "X-Nummem-UI": "browser" },
  });
  if (response.status === 401) {
    throw new Error("Authentication required");
  }
  if (!response.ok) {
    throw new Error("Failed to load saved associations");
  }
  const payload = (await response.json()) as SavedAssociationCollection;
  if (payload.items.some(
    (association) =>
      (association.encoding_system ?? "english") !== encodingSystem,
  )) {
    throw new Error("Saved associations contain a different encoding system");
  }
  return payload;
}

export async function saveAssociation(
  association: AssociationMapping,
): Promise<SavedAssociation> {
  const response = await fetch("/ui-api/saved-associations", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "X-Nummem-UI": "browser",
    },
    body: JSON.stringify(association),
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(error?.detail ?? "Failed to save association");
  }
  const payload = (await response.json()) as SavedAssociation;
  const expected = association.encoding_system ?? "english";
  if ((payload.encoding_system ?? "english") !== expected) {
    throw new Error("Saved association contains a different encoding system");
  }
  return payload;
}

export async function validateAssociation(
  association: AssociationMapping,
): Promise<AssociationMapping> {
  const response = await fetch("/ui-api/saved-associations/validate", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "X-Nummem-UI": "browser",
    },
    body: JSON.stringify(association),
  });
  if (response.status === 401) {
    throw new Error("Authentication required");
  }
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(error?.detail ?? "Invalid or unsupported share link");
  }
  const payload = (await response.json()) as AssociationMapping;
  const expected = association.encoding_system ?? "english";
  if ((payload.encoding_system ?? "english") !== expected) {
    throw new Error("Validated association contains a different encoding system");
  }
  return payload;
}

export async function deleteSavedAssociation(associationId: string): Promise<void> {
  const response = await fetch(`/ui-api/saved-associations/${associationId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-Nummem-UI": "browser" },
  });
  if (!response.ok) {
    throw new Error("Failed to remove saved association");
  }
}

function validateBatchEncodingSystem(
  response: BatchEncodeResponse,
  expected: EncodingSystem,
) {
  const profile = getEncodingProfile(expected);
  if ((response.encoding_system ?? "english") !== expected) {
    throw new Error("Encoding response contains a different encoding system");
  }
  for (const item of response.items) {
    if (!item.ok) {
      continue;
    }
    if ((item.result.encoding_system ?? "english") !== expected) {
      throw new Error("Encoding result contains a different encoding system");
    }
    if (
      profile.requiresLocalizedCandidateMetadata &&
      item.result.candidates.some(
        (candidate) => !candidateMetadataMatchesProfile(candidate, expected),
      )
    ) {
      throw new Error(
        `${profile.displayName} result contains non-${profile.displayName} metadata`,
      );
    }
  }
}

export async function updateProductEmails(enabled: boolean): Promise<void> {
  const response = await fetch("/privacy/me/product-emails", {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) {
    throw new Error("Failed to update product-email preference");
  }
}
